import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { authAPI, unterschriftAPI } from '@/lib/api';
import { tokenStore } from '@/lib/tokenStore';

/* Kiosk-Grundlage (Bauauftrag Unterschrift-Station 03.10.2026, Modul B) — fuer die dauerhaft
   laufenden Stations-iPads (Unterschrift an der Theke, Anwesenheit an der Saeule). */

/** Bildschirm wach halten, solange die Seite sichtbar ist (Screen Wake Lock; iPad Safari ab 16.4).
    Der Browser gibt die Sperre frei, sobald die Seite in den Hintergrund geht — beim Zurueckkommen
    wird sie neu angefordert. Ohne Unterstuetzung passiert nichts. */
export function useWachBleiben(aktiv = true) {
  useEffect(() => {
    if (!aktiv || !navigator.wakeLock?.request) return undefined;
    let sperre = null;
    let beendet = false;
    const anfordern = async () => {
      try {
        const s = await navigator.wakeLock.request('screen');
        if (beendet) s.release().catch(() => {}); else sperre = s;
      } catch { /* z. B. Energiesparmodus — dann eben nicht */ }
    };
    const beiSichtbar = () => { if (document.visibilityState === 'visible') anfordern(); };
    anfordern();
    document.addEventListener('visibilitychange', beiSichtbar);
    return () => {
      beendet = true;
      document.removeEventListener('visibilitychange', beiSichtbar);
      sperre?.release?.().catch(() => {});
    };
  }, [aktiv]);
}

const SECHS_STUNDEN = 6 * 60 * 60 * 1000;

/** Gleitende Sitzung: Stations-Rollen bekommen beim Laden und danach alle sechs Stunden ein frisches
    30-Tage-Token (POST /auth/verlaengern). Fuer alle anderen Rollen antwortet der Server mit
    „nicht verlaengert" und es aendert sich nichts. */
export function useSitzungVerlaengern(aktiv = true) {
  useEffect(() => {
    if (!aktiv) return undefined;
    let letzte = 0;
    const verlaengern = async () => {
      if (Date.now() - letzte < 60 * 1000) return;
      letzte = Date.now();
      try {
        const r = await authAPI.verlaengern();
        if (r.data?.verlaengert && r.data.token) tokenStore.setToken(r.data.token);
      } catch { /* abgelaufen: die Seite zeigt selbst „Bitte neu anmelden" */ }
    };
    verlaengern();
    const t = setInterval(verlaengern, SECHS_STUNDEN);
    const beiSichtbar = () => { if (document.visibilityState === 'visible') verlaengern(); };
    document.addEventListener('visibilitychange', beiSichtbar);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', beiSichtbar); };
  }, [aktiv]);
}


/** „Zur Unterschrift senden" fuer die Dialoge am Mitglied (Bestandsvertrag, neue Laufzeit,
    Zusatzvereinbarung): sendet den Entwurf an die Station und haelt den Vorgang, bis der Kunde
    unterschrieben hat. Die Anzeige uebernimmt <SendenInhalt>. */
export function useStationSenden(art, onErgebnis) {
  const [vorgang, setVorgang] = useState(null);
  const [sendet, setSendet] = useState(false);
  const letzter = useRef(null);
  const senden = useCallback(async (entwurf) => {
    letzter.current = entwurf;
    setSendet(true);
    try {
      const r = await unterschriftAPI.senden(entwurf, art);
      setVorgang(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Konnte nicht zur Unterschrift gesendet werden');
    }
    setSendet(false);
  }, [art]);
  const erneut = useCallback(() => { if (letzter.current) senden(letzter.current); }, [senden]);
  const schliessen = useCallback(() => setVorgang(null), []);
  const fertig = useCallback((ergebnis) => { setVorgang(null); onErgebnis?.(ergebnis); }, [onErgebnis]);
  return { vorgang, sendet, senden, erneut, schliessen, fertig };
}
